import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import en from "./messages/en.json";
import am from "./messages/am.json";
import ti from "./messages/ti.json";
import fr from "./messages/fr.json";
import de from "./messages/de.json";
import ar from "./messages/ar.json";
import sw from "./messages/sw.json";
import so from "./messages/so.json";
import om from "./messages/om.json";

const dictionaries = { en, am, ti, fr, de, ar, sw, so, om } as const;
export type Locale = keyof typeof dictionaries;

const I18nContext = createContext<Locale>("en");

function detectLocale(): Locale {
  if (typeof window === "undefined") return "en";
  try {
    const stored = localStorage.getItem("edaga.locale") as Locale | null;
    if (stored && stored in dictionaries) return stored;
    const browser = navigator.language.split("-")[0] as Locale;
    return browser in dictionaries ? browser : "en";
  } catch {
    return "en";
  }
}

export function I18nProvider({ children, initialLocale }: { children: ReactNode; initialLocale?: Locale }) {
  const [locale, setLocale] = useState<Locale>(initialLocale ?? detectLocale);

  useEffect(() => {
    if (initialLocale) setLocale(detectLocale());
  }, [initialLocale]);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
  }, [locale]);
  return <I18nContext.Provider value={locale}>{children}</I18nContext.Provider>;
}

export function useLocale() {
  return useContext(I18nContext);
}

function lookup(dictionary: unknown, key: string): unknown {
  return key.split(".").reduce<unknown>((value, part) => {
    if (typeof value !== "object" || value === null) return undefined;
    return (value as Record<string, unknown>)[part];
  }, dictionary);
}

function formatPluralBlocks(template: string, values: Record<string, string | number>, locale: Locale) {
  let result = template;
  const startPattern = /\{(\w+),\s*plural,\s*/g;

  for (let match = startPattern.exec(result); match; match = startPattern.exec(result)) {
    const value = values[match[1]];
    if (typeof value !== "number") continue;

    let depth = 1;
    let end = match.index + match[0].length;
    for (; end < result.length && depth > 0; end += 1) {
      if (result[end] === "{") depth += 1;
      if (result[end] === "}") depth -= 1;
    }
    if (depth !== 0) break;

    const body = result.slice(match.index + match[0].length, end - 1);
    const choices = new Map<string, string>();
    const choicePattern = /(=\d+|zero|one|two|few|many|other)\s*\{/g;
    for (let choice = choicePattern.exec(body); choice; choice = choicePattern.exec(body)) {
      let choiceDepth = 1;
      let choiceEnd = choice.index + choice[0].length;
      for (; choiceEnd < body.length && choiceDepth > 0; choiceEnd += 1) {
        if (body[choiceEnd] === "{") choiceDepth += 1;
        if (body[choiceEnd] === "}") choiceDepth -= 1;
      }
      choices.set(choice[1], body.slice(choice.index + choice[0].length, choiceEnd - 1));
      choicePattern.lastIndex = choiceEnd;
    }

    const exact = choices.get(`=${value}`);
    const category = new Intl.PluralRules(locale).select(value);
    const replacement = (exact ?? choices.get(category) ?? choices.get("other") ?? "")
      .replaceAll("#", String(value));
    result = `${result.slice(0, match.index)}${replacement}${result.slice(end)}`;
    startPattern.lastIndex = match.index + replacement.length;
  }

  return result;
}

export function useTranslations(namespace?: string) {
  const locale = useLocale();
  return (key: string, values?: Record<string, string | number>) => {
    const fullKey = namespace ? `${namespace}.${key}` : key;
    const localized = lookup(dictionaries[locale], fullKey);
    const fallback = lookup(dictionaries.en, fullKey);
    let result = typeof localized === "string" ? localized : typeof fallback === "string" ? fallback : key;
    result = formatPluralBlocks(result, values ?? {}, locale);
    Object.entries(values ?? {}).forEach(([name, value]) => {
      result = result.replaceAll(`{${name}}`, String(value));
    });
    return result;
  };
}
